/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint no-param-reassign: "error" */
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  uploadSingleChunk,
  validateUploadParams,
  initializeUploadWithDebounce,
  finalizeUploadWithRetry,
  ChunkUploadParams,
} from './utils/uploadUtils';
import Recorder from './Recorder/utils/recorderLogic';
import { RecorderState } from './store';
import {
  cleanup,
  getDisplayMediaStream,
  getUserMediaStream,
  streams,
} from './Recorder/utils/streamUtils';
import logger from '../lib/logger';
import { FileType } from '../types';
import {
  addBackupRecord,
  saveChunk,
  deleteBackupRecord,
  deleteChunks,
  updateBackupRecordStatus,
} from '../lib/recovery';
import { setMicrophoneAudio } from './recorderSlice';
import trackRecorderError from './utils/errorTracking';
import { pauseMultipartUpload } from './uploadService';
import { EventEmitter } from './EventEmitter';
import { shouldRefreshToken } from '../lib/localStorageHandler';
import { refreshToken } from '../features/auth/authThunks';
import { globalStorageCircuitBreaker } from '../lib/storageQuota';

let recorder: Recorder | undefined;
export const getRecorder = () => recorder;

const getSerializedDevice = ({
  deviceId,
  groupId,
  label,
  kind,
}: MediaDeviceInfo) => ({
  deviceId,
  groupId,
  label,
  kind,
});

async function checkMediaPermissions() {
  try {
    const [cameraPerm, microphonePerm] = await Promise.all([
      navigator.permissions.query({
        name: 'camera' as any,
      }),
      navigator.permissions.query({
        name: 'microphone' as any,
      }),
    ]);
    return {
      camera: cameraPerm.state,
      microphone: microphonePerm.state,
    };
  } catch (error) {
    return { camera: 'prompt', microphone: 'prompt' };
  }
}

/**
 * For device enumeration to work, the permission must have been already
 * obtained by calling navigator.mediaDevices.getUserMedia.
 * This function is a hack to get the devices to enumerate.
 *
 * @param camera - Whether the camera is requested
 * @param microphone - Whether the microphone is requested
 */
async function enumerateDevicesHack({
  camera,
  microphone,
}: {
  camera: boolean;
  microphone: boolean;
}): Promise<void> {
  const { camera: cameraPermission, microphone: microphonePermission } =
    await checkMediaPermissions();
  const toRequest: Record<string, boolean> = {};
  if (camera && cameraPermission === 'prompt') {
    toRequest.video = true;
  }
  if (microphone && microphonePermission === 'prompt') {
    toRequest.audio = true;
  }
  if (Object.keys(toRequest).length === 0) {
    return;
  }

  try {
    const obtainedStreams =
      await navigator.mediaDevices.getUserMedia(toRequest);
    if (obtainedStreams) {
      obtainedStreams.getTracks().forEach((track) => track.stop());
    }
  } catch (error) {
    if (toRequest.video && toRequest.audio) {
      // If both video and audio are requested, try to enumerate devices without one of them
      await Promise.all([
        enumerateDevicesHack({ camera: false, microphone: true }),
        enumerateDevicesHack({ camera: true, microphone: false }),
      ]);
    }
  }
}

export const fetchAvailableDevices = createAsyncThunk(
  'recorder/devices/fetch',
  async (
    { camera, microphone }: { camera: boolean; microphone: boolean } = {
      camera: true,
      microphone: true,
    },
    { rejectWithValue },
  ) => {
    try {
      await enumerateDevicesHack({ camera, microphone });

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoinput = devices
        .filter((device) => device.kind === 'videoinput')
        .map(getSerializedDevice);
      const audioinput = devices
        .filter((device) => device.kind === 'audioinput')
        .map(getSerializedDevice);
      return { videoinput, audioinput };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchUserMediaThunk = createAsyncThunk(
  'recorder/userMedia/fetch',
  async (
    constraints: Parameters<typeof navigator.mediaDevices.getUserMedia>[0],
  ): Promise<{
    streamId: string;
    trackInfo: Pick<MediaStreamTrack, 'id' | 'kind' | 'label'>[];
  }> => {
    try {
      const { streamId, trackInfo } = await getUserMediaStream(constraints);
      return { streamId, trackInfo };
    } catch (error) {
      logger.error('Error fetching user media', error);
      throw error;
    }
  },
);

export const fetchDisplayMediaThunk = createAsyncThunk(
  'recorder/displayMedia/fetch',
  async (
    displayMediaConstraints: Parameters<MediaDevices['getDisplayMedia']>[0],
    { dispatch },
  ) => {
    try {
      const { streamId, trackInfo } = await getDisplayMediaStream(
        displayMediaConstraints,
      );
      return { streamId, trackInfo };
    } catch (error) {
      logger.log('Error fetching display media', error);
      throw error;
    }
  },
);

export const initializeUploadThunk = createAsyncThunk(
  'recorder/upload/initialize',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const { teamId, folderId, contentType } = (getState() as RecorderState)
        .recorder.upload;
      if (!teamId || !folderId || !contentType)
        throw new Error('Missing required parameters');

      const { fileId, uploadId } = await initializeUploadWithDebounce({
        teamId,
        folderId,
        contentType,
      });

      return {
        fileId,
        uploadId,
      };
    } catch (error) {
      // Clean up any active media streams immediately
      cleanup();

      // CRITICAL: Direct title bar notification - most reliable method
      // This MUST work for screen sharing scenarios
      if (typeof document !== 'undefined') {
        const originalTitle = document.title;
        document.title = '❌ Recording failed to start - check your connection';

        // Store original title for restoration
        (window as any).__recorderOriginalTitle = originalTitle;

        // Restore original title after 10 seconds (user can also click to dismiss)
        setTimeout(() => {
          if (
            document.title ===
            '❌ Recording failed to start - check your connection'
          ) {
            document.title =
              (window as any).__recorderOriginalTitle || originalTitle;
          }
        }, 10000);
      }

      // FIXED: Track initialization errors in PostHog for monitoring
      const state = getState() as RecorderState;
      trackRecorderError({
        operation: 'initialization',
        errorType: error?.name || 'InitializationError',
        errorMessage: error?.message || 'Upload initialization failed',
        errorStack: error?.stack,
        additionalContext: {
          teamId: state.recorder.upload.teamId,
          folderId: state.recorder.upload.folderId,
          contentType: state.recorder.upload.contentType,
        },
      });
      return rejectWithValue(error);
    }
  },
);

export const createBackupRecordThunk = createAsyncThunk(
  'recorder/recording/backup/create',
  async (_, { getState }) => {
    try {
      const state = getState() as RecorderState;
      const { teamId, folderId, fileId, uploadId } = state.recorder.upload;

      // Recording will only start if initialization succeeded, so fileId is guaranteed to exist
      if (!fileId) {
        throw new Error(
          'Cannot create backup record: recording should not have started without fileId',
        );
      }

      await addBackupRecord(fileId, {
        teamId: teamId || undefined,
        folderId: folderId || undefined,
        fileId,
        uploadId: uploadId || undefined,
        startedAt: Date.now(),
        status: 'pending',
      });
    } catch (error) {
      console.error('Failed to create backup record:', error);
      // FIXED: Track backup record errors in PostHog for monitoring
      const errorState = getState() as RecorderState;
      trackRecorderError({
        operation: 'backup_record',
        errorType: error?.name || 'BackupError',
        errorMessage: error?.message || 'Failed to create backup record',
        errorStack: error?.stack,
        fileId: errorState.recorder.upload.fileId,
      });
    }

    return true;
  },
);

export const startRecordingThunk = createAsyncThunk(
  'recorder/recording/start',
  async (_, { dispatch, getState }) => {
    // Check if token needs refresh before starting recording
    if (shouldRefreshToken()) {
      await dispatch(refreshToken());
    }

    // Check storage availability before starting
    const canRecord = await globalStorageCircuitBreaker.shouldAllowOperation(
      50 * 1024 * 1024, // Check if at least 50MB free
    );

    if (!canRecord) {
      logger.warn(
        'Low storage detected - starting recording with backup disabled, upload-only mode',
      );

      // Update document title to alert user (but still allow recording)
      if (typeof document !== 'undefined') {
        const originalTitle = document.title;
        document.title = '⚠️ Low storage - backup disabled, upload continues';
        (window as any).__recorderOriginalTitle = originalTitle;

        setTimeout(() => {
          if (
            document.title ===
            '⚠️ Low storage - backup disabled, upload continues'
          ) {
            document.title = originalTitle;
          }
        }, 15000);
      }

      // Continue with recording - backup will be skipped, but upload will work
      logger.info('Recording will start in upload-only mode (no local backup)');
    }

    if (recorder) {
      await recorder.stopRecording();
      throw new Error('Recorder already exists. Cannot start a new recording');
    }
    const { upload } = (getState() as RecorderState).recorder;
    const { userMediaStream, displayMediaStream } = streams;
    const { contentType } = upload;
    if (!userMediaStream || !displayMediaStream || !contentType) {
      throw new Error(
        'Missing camera/display streams or contentType to start recording',
      );
    }
    recorder = new Recorder({
      contentType,
      settings: (getState() as RecorderState).recorder.settings,
    });
    await recorder.startRecording();
  },
);

// Dispatch calls to upload service logic
export const uploadChunks = createAsyncThunk(
  'recorder/upload/chunk',
  async (
    {
      blob,
      partNumber,
    }: {
      blob: Blob;
      partNumber: number;
    },
    { getState },
  ) => {
    const state = getState() as RecorderState;
    const uploadState = state.recorder.upload;

    // Validate required parameters and get validated non-null values
    const validatedParams = validateUploadParams({
      teamId: uploadState.teamId,
      folderId: uploadState.folderId,
      fileId: uploadState.fileId,
      uploadId: uploadState.uploadId,
      contentType: uploadState.contentType,
    });

    // Upload the chunk using our tested utility function
    const uploadParams: ChunkUploadParams = {
      ...validatedParams,
      blob,
      partNumber,
    };

    await uploadSingleChunk(uploadParams);
  },
);

export const backupChunkThunk = createAsyncThunk(
  'recorder/recording/backup/chunk',
  async (
    {
      blob,
      partNumber,
    }: {
      blob: Blob;
      partNumber: number;
    },
    { getState, dispatch },
  ) => {
    const state = getState() as RecorderState;

    try {
      const { fileId } = state.recorder.upload;

      if (!fileId) {
        throw new Error(
          'Cannot backup chunk: recording should not have started without fileId',
        );
      }

      // Check circuit breaker before attempting backup
      const canProceed = await globalStorageCircuitBreaker.shouldAllowOperation(
        blob.size,
      );

      if (!canProceed) {
        logger.warn(
          'Storage circuit breaker OPEN - skipping IndexedDB backup but continuing upload',
        );

        // Update document title to alert user (but don't stop recording)
        if (typeof document !== 'undefined') {
          const currentTitle = document.title;
          if (!currentTitle.includes('⚠️')) {
            document.title =
              '⚠️ Low storage - backup disabled, upload continues';
            (window as any).__recorderOriginalTitle =
              (window as any).__recorderOriginalTitle || currentTitle;

            setTimeout(() => {
              if (
                document.title ===
                '⚠️ Low storage - backup disabled, upload continues'
              ) {
                document.title =
                  (window as any).__recorderOriginalTitle || currentTitle;
              }
            }, 15000);
          }
        }

        // Skip IndexedDB backup but allow recording to continue
        // The multipart upload will still work independently
        logger.info(
          `Skipping backup for chunk ${partNumber} due to low storage - multipart upload will continue`,
        );
        return true; // Return success to allow recording to continue
      }

      await saveChunk(fileId, partNumber, blob);
    } catch (error) {
      console.error('Failed to backup chunk:', error);
      // FIXED: Track backup chunk errors in PostHog for monitoring
      trackRecorderError({
        operation: 'backup_chunk',
        errorType: error?.name || 'BackupError',
        errorMessage: error?.message || 'Failed to backup chunk',
        errorStack: error?.stack,
        fileId: state.recorder.upload.fileId,
        partNumber,
      });

      // Don't re-throw - backup failures should not stop recording
      // The multipart upload is the critical path, backup is optional
      logger.warn(
        `Backup failed for chunk ${partNumber}, but recording continues`,
      );
    }

    return true;
  },
);

export const pauseRecordingThunk = createAsyncThunk(
  'recorder/recording/pause',
  async (_, { getState }) => {
    if (recorder) {
      recorder.pauseRecording();
    }

    // After pausing the MediaRecorder, inform backend
    try {
      const { teamId, folderId, fileId } = (getState() as RecorderState)
        .recorder.upload;

      if (fileId && teamId && folderId) {
        // Fire and forget – do not block UI on this call
        pauseMultipartUpload({ teamId, folderId, fileId }).catch((err) => {
          logger.error('Pause endpoint call failed', err);
        });
      }
    } catch (error) {
      // Swallow error; UI should not break if pause call fails
      console.error('Failed to notify backend about pause', error);
    }
  },
);

export const resumeRecordingThunk = createAsyncThunk(
  'recorder/recording/resume',
  async () => {
    if (recorder) {
      recorder.resumeRecording();
    }
  },
);

export const stopRecordingThunk = createAsyncThunk(
  'recorder/recording/stop',
  async (_, { dispatch }) => {
    if (recorder) {
      await recorder.stopRecording();
      recorder = undefined;
    }
  },
);

export const muteMicrophoneThunk = createAsyncThunk(
  'recorder/microphone/mute',
  async (_, { dispatch }) => {
    if (recorder) {
      await recorder.muteMicrophone();
      dispatch(setMicrophoneAudio(false));
    }
  },
);

export const unmuteMicrophoneThunk = createAsyncThunk(
  'recorder/microphone/unmute',
  async (_, { dispatch }) => {
    if (recorder) {
      await recorder.unmuteMicrophone();
      dispatch(setMicrophoneAudio(true));
    }
  },
);

export const updateMicrophoneSourceThunk = createAsyncThunk(
  'recorder/microphone/updateSource',
  async () => {
    if (recorder) {
      await recorder.updateMicrophoneSource();
    }
  },
);

export const finalizeUpload = createAsyncThunk(
  'recorder/upload/finalize',
  async (_, { getState }): Promise<FileType> => {
    const state = getState() as RecorderState;
    const uploadState = state.recorder.upload;

    // Validate required parameters
    if (
      !uploadState.teamId ||
      !uploadState.folderId ||
      !uploadState.fileId ||
      !uploadState.uploadId ||
      !uploadState.contentType
    ) {
      console.error(
        `Missing required parameters (teamId, folderId, fileId, uploadId, contentType). Cannot finalize upload. ${JSON.stringify(
          uploadState,
        )}`,
      );
      throw new Error('Missing required parameters for finalization');
    }

    // Use the utility function with retry logic
    const result = await finalizeUploadWithRetry({
      teamId: uploadState.teamId,
      folderId: uploadState.folderId,
      fileId: uploadState.fileId,
      uploadId: uploadState.uploadId,
      contentType: uploadState.contentType,
      notes: uploadState.notes || [],
      title: uploadState.title || undefined,
    });

    // Mark backup record as uploaded since upload was successful
    // Note: We still keep the backup record in case something goes wrong in the backend - they'll be cleaned up by the cleanup logic
    try {
      await updateBackupRecordStatus(uploadState.fileId, 'uploaded');
      logger.info(
        `Marked backup record as uploaded for successful upload: ${uploadState.fileId}`,
      );
    } catch (error) {
      // Don't fail the upload if backup status update fails - just log the error
      logger.warn(
        `Failed to update backup record status for ${uploadState.fileId}:`,
        error,
      );
    }

    return result;
  },
);

export const cancelThunk = createAsyncThunk('recorder/cancelThunk', () => {
  cleanup();
});
